<template>
  <div class="attendance-mobile-container py-2 pb-16">
    <!-- Progress Indicator -->
    <div v-if="attendancePage.loadTable" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="44"></v-progress-circular>
      <div class="mt-2 text-xs text-gray-500">Loading attendance data...</div>
    </div>

    <!-- Attendance Cards List -->
    <div v-else-if="attendancePage.items && attendancePage.items.length > 0">
      <v-card
        v-for="item in attendancePage.items"
        :key="item.employeeId"
        outlined
        class="attendance-card mb-4 rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <!-- Card Header: Avatar & Name -->
        <v-card-text class="pb-2 pt-4 px-4 bg-gray-50 flex justify-between items-center">
          <UserAvatar
            :user="item"
            :size="40"
            :show-name="true"
            :tooltip-enabled="false"
            :show-mask-details="false"
            name-class="text-sm font-bold text-gray-800"
          />
        </v-card-text>

        <!-- Card Body: Attendance Summary & Expansion Panel -->
        <v-card-text class="px-4 py-3 border-t">
          <!-- Summary Row -->
          <div class="grid grid-cols-3 gap-2 text-center bg-gray-50 p-2 rounded-lg mb-3">
            <div>
              <span class="text-[10px] text-gray-400 block uppercase font-bold">Present</span>
              <span class="text-sm font-extrabold text-green-600">{{ getSummaryCount(item, 'PRESENT') }}</span>
            </div>
            <div>
              <span class="text-[10px] text-gray-400 block uppercase font-bold">Absent</span>
              <span class="text-sm font-extrabold text-red-500">{{ getSummaryCount(item, 'ABSENT') }}</span>
            </div>
            <div>
              <span class="text-[10px] text-gray-400 block uppercase font-bold">Anomaly</span>
              <span class="text-sm font-extrabold text-orange-500">{{ getAnomalyCount(item) }}</span>
            </div>
          </div>

          <!-- Daily Details Accordion -->
          <v-expansion-panels flat outlined class="border rounded-lg overflow-hidden">
            <v-expansion-panel>
              <v-expansion-panel-header class="py-2 px-3 text-xs font-bold text-gray-700 bg-white">
                View Daily Attendance Logs
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-list dense class="pa-0 max-h-[300px] overflow-y-auto">
                  <v-list-item
                    v-for="date in attendancePage.currentMonthDates"
                    :key="date"
                    class="border-b py-2 px-3 flex justify-between items-center gap-2"
                  >
                    <div>
                      <div class="font-bold text-xs text-gray-800">
                        {{ date }} ({{ attendancePage.dayOfMonth(date) }})
                      </div>
                      <!-- Check-In / Out times -->
                      <div class="text-[10px] text-gray-500 mt-0.5">
                        <span v-if="item.attendance[date]?.checkInTime">
                          In: {{ item.attendance[date].checkInTime }}
                        </span>
                        <span v-if="item.attendance[date]?.checkOutTime" class="ml-2">
                          Out: {{ item.attendance[date].checkOutTime }}
                        </span>
                        <span v-if="!item.attendance[date]?.checkInTime && !item.attendance[date]?.checkOutTime">
                          No punch logs
                        </span>
                      </div>
                    </div>

                    <!-- Right side status & action buttons -->
                    <div class="flex items-center gap-2">
                      <!-- Status Badge -->
                      <span :style="getStatusStyle(item.attendance[date])" class="text-[10px] px-2 py-0.5 rounded font-bold uppercase border">
                        {{ getStatusLabel(item.attendance[date]) }}
                      </span>

                      <!-- Take Action button for corrections -->
                      <v-btn
                        v-if="attendancePage.isAnyErrorInAttendance(item.attendance[date]?.checkInOutData)"
                        x-small
                        depressed
                        color="primary"
                        class="white--text text-[10px] px-2 normal-case font-semibold"
                        @click.stop="attendancePage.openAttendanceDialog(item, date)"
                      >
                        Action
                      </v-btn>
                    </div>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </div>

    <!-- No Data Placeholder -->
    <no-data
      v-else
      title="Nothing to Display"
      subTitle="Please select the custom date range"
    ></no-data>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'
import NoData from '@/components/NoData.vue'

export default {
  name: 'AttendancePage_mobile',
  components: {
    UserAvatar,
    NoData
  },
  inject: ['attendancePage'],
  methods: {
    getSummaryCount(item, status) {
      if (!item.attendance) return 0
      let count = 0
      Object.keys(item.attendance).forEach(date => {
        if (item.attendance[date]?.status === status) {
          count++
        }
      })
      return count
    },
    getAnomalyCount(item) {
      if (!item.attendance) return 0
      let count = 0
      Object.keys(item.attendance).forEach(date => {
        const checkInOut = item.attendance[date]?.checkInOutData || []
        if (this.attendancePage.isAnyErrorInAttendance(checkInOut)) {
          count++
        }
      })
      return count
    },
    getStatusLabel(attendanceRecord) {
      if (!attendanceRecord) return '-'
      return attendanceRecord.status || 'ABSENT'
    },
    getStatusStyle(attendanceRecord) {
      const status = this.getStatusLabel(attendanceRecord)
      const colors = {
        PRESENT: { color: '#2e7d32', bg: '#e8f5e9', border: '#c8e6c9' },
        ABSENT: { color: '#c62828', bg: '#ffebee', border: '#ffcdd2' },
        WEEK_OFF: { color: '#37474f', bg: '#eceff1', border: '#cfd8dc' },
        PUBLIC_HOLIDAY: { color: '#1565c0', bg: '#e3f2fd', border: '#bbdefb' },
        LEAVE: { color: '#ef6c00', bg: '#fff3e0', border: '#ffe0b2' }
      }
      const style = colors[status] || { color: '#757575', bg: '#f5f5f5', border: '#e0e0e0' }
      return {
        color: style.color,
        backgroundColor: style.bg,
        borderColor: style.border
      }
    }
  }
}
</script>

<style scoped>
.attendance-card {
  border-radius: 12px !important;
}
</style>
